import React, {
  FC,
  HTMLProps,
  ReactNode,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Meta } from "rc-field-form/es/interface";
import cn from "classnames";
import stl from "./Input.module.scss";
import { nanoid } from "nanoid";
import { useHtmlStates } from "../../hooks";
import { isArrayFilled } from "../../utils";

type Value = string;
interface Props
  extends Omit<HTMLProps<HTMLInputElement>, "onChange" | "type" | "size"> {
  value?: Value;
  onChange?: (v: Value) => void;
  wrapProps?: HTMLProps<HTMLDivElement>;
  labelProps?: HTMLProps<HTMLLabelElement>;
  unstyled?: boolean;
  fieldMeta?: Meta;
  type?: "text" | "number" | "password" | "tel" | "search";
  variant?: "search";
  size?: "large" | "small";
  append?: ReactNode;
  prepend?: ReactNode;
}
const Input: FC<Props> = (componentProps) => {
  const [{ isActive, isFocused, isHovered }, wrapRef] = useHtmlStates();

  const {
    id = nanoid(),
    value,
    onChange,
    label,
    wrapProps,
    labelProps,
    unstyled,
    fieldMeta,
    type = "text",
    variant,
    size,
    append,
    prepend,
    ...props
  } = componentProps;

  // START value handling
  const [localValue, setLocalValue] = useState<Value>("");

  const onChanges = (v: string) => {
    setLocalValue(v);
    onChange?.(v);
  };

  useEffect(() => {
    setLocalValue(value ?? "");
  }, [value]);

  const displayValue = value ?? localValue;
  // END value handling

  const placeholder = useMemo(() => {
    return isFocused
      ? !displayValue.length
        ? ""
        : displayValue
      : props.placeholder;
  }, [isFocused, props.placeholder, displayValue]);

  return (
    <div
      ref={wrapRef}
      className={cn(wrapProps?.className, stl.block, {
        [stl.unstyled]: unstyled,
        [stl.errored]: isArrayFilled(fieldMeta?.errors),
        [stl.filled]: displayValue.length > 0,
        [stl.active]: isActive,
        [stl.focus]: isFocused,
        [stl.hover]: isHovered,
        [stl.search]: variant === "search",
        [stl.large]: size === "large",
        [stl.small]: size === "small",
        [stl.hasAppend]: append,
        [stl.hasPrepend]: prepend,
      })}
      {...wrapProps}
    >
      {label && (
        <label
          htmlFor={id}
          {...labelProps}
          className={cn(labelProps?.className, {
            [stl.labelShrink]: displayValue.length > 0 || isFocused,
          })}
        >
          {label}
        </label>
      )}
      {prepend && <span className={stl.prepend}>{prepend}</span>}
      <input
        {...props}
        type={type}
        placeholder={placeholder}
        id={id}
        name={fieldMeta?.name?.join(".")}
        value={displayValue}
        onChange={(e) => onChanges(e.currentTarget.value)}
      />
      {append && <span className={stl.append}>{append}</span>}
    </div>
  );
};

export default Input;
